"use client";

import { useSearchParams } from "next/navigation";
import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { OAuthProvider } from "@/components/oauth-provider-buttons";
import { type SyncStep, useSyncStatus } from "@/components/sync-status-dialog";
import { useBackupImport } from "@/hooks/use-backup-import";
import { useBapProfile } from "@/hooks/use-bap-profile";
import { useClientTheme } from "@/hooks/use-client-theme";
import { useEmailOtpSignIn } from "@/hooks/use-email-otp-sign-in";
import { useImportShortcut } from "@/hooks/use-import-shortcut";
import { type OAuthClientInfo, useOAuthClientInfo } from "@/hooks/use-login-queries";
import { usePRF } from "@/hooks/use-prf";
import { clearPendingRecoveryMethod, completeRecoveryMethod, rememberSuccessfulSignIn, stageRecoveryMethod } from "@/lib/auth/last-sign-in-method";
import { buildRestoreCallbackURL, encryptAndSaveBackup, executePasswordLogin, getOAuthClientErrorMessage, handlePostSignInRedirect, LoginFlowError, MAC_PLATFORM_REGEX, performSignIn, postSignInIdentitySync, UNREGISTERED_IDENTITY_CODE } from "@/lib/auth/login-flow";
import { recoveryErrorMessage } from "@/lib/auth/recovery-error-message";
import { authClient } from "@/lib/auth-client";
import { type IdentityBackup, validateIdentityBackup } from "@/lib/bap/identity-backup";
import { extractIdentityFromBackup } from "@/lib/bap/utils";
import { fetchCloudBackup, unlockCloudBackup } from "@/lib/cloud-unlock";
import { useFileDrop } from "@/lib/hooks/use-file-drop";
import { describeLocalWallet } from "@/lib/local-wallet-identity";
import { logger } from "@/lib/logger";
import { getOAuthCallbackErrorMessage } from "@/lib/oauth-account-linking-errors";
import { STORAGE_KEYS } from "@/lib/storage-keys";
import type { ClientTheme } from "@/lib/theme-resolver";
import { hydratePRFCredentialFromServer } from "@/lib/webauthn/prf-server-persistence";

/**
 * State, effects and handlers behind the login screen. Extracted verbatim from
 * LoginContent in app/(auth)/login/page.tsx (OPL-4289) with the hook order
 * preserved exactly; the page keeps only the composition and the markup.
 */
function recoverySource(): string {
  return JSON.stringify([localStorage.getItem(STORAGE_KEYS.ENCRYPTED_BACKUP), localStorage.getItem(STORAGE_KEYS.CURRENT_IDENTITY), sessionStorage.getItem(STORAGE_KEYS.DECRYPTED_BACKUP), sessionStorage.getItem(STORAGE_KEYS.ACTIVE_ACCOUNT)]);
}
async function requestRecoveryProviderURL(provider: OAuthProvider, callbackURL: string, errorCallbackURL: string): Promise<string> {
  const result = await authClient.signIn.social({
    provider,
    callbackURL,
    newUserCallbackURL: callbackURL,
    errorCallbackURL,
    disableRedirect: true,
    requestSignUp: false
  });
  if (result.error || !result.data?.url) {
    throw new Error("Provider sign-in was not completed. Please try again.");
  }
  return result.data.url;
}
export interface LoginPageServerData {
  /** OAuth client resolved server-side from `client_id`, or null */
  initialClient: OAuthClientInfo | null;
  /** White-label theme resolved server-side, or null */
  serverTheme: ClientTheme | null;
}

/**
 * Log the cause (decrypt, validate, sign-in, sync) for operators and return
 * the line the user sees. Our own thrown messages are already written for
 * the user ("Incorrect password…", "Unsupported backup format"), so they are
 * shown as-is; anything else gets the generic line.
 */
function reportPasswordLoginFailure(loginError: unknown): string {
  const message = loginError instanceof Error ? loginError.message : "";
  logger.error({
    operation: "password_login",
    errorName: loginError instanceof Error ? loginError.name : "UnknownError",
    errorMessage: message || String(loginError)
  }, "Password login failed");
  return message || "Login failed. Check your password and try again.";
}
export function useLoginPage(serverData?: LoginPageServerData) {
  const searchParams = useSearchParams();
  const [password, setPassword] = useState("");
  // Local storage is read after mount, not in a lazy initializer: the
  // server has no storage, so initializing from it here rendered "Welcome"
  // on the server and "Unlock Your ID" on the client (hydration mismatch).
  // The card is held back until the check has run so nothing flashes.
  const [storageChecked, setStorageChecked] = useState(false);
  const [hasLocalBackup, setHasLocalBackup] = useState(false);
  // Whose wallet is on this device (OPL-4241): the cached current BAP id plus
  // the public profile lookup. Never implies a server session exists.
  const [localWalletBapId, setLocalWalletBapId] = useState<string | null>(null);
  useEffect(() => {
    setHasLocalBackup(!!localStorage.getItem(STORAGE_KEYS.ENCRYPTED_BACKUP));
    setLocalWalletBapId(localStorage.getItem(STORAGE_KEYS.CURRENT_IDENTITY));
    setStorageChecked(true);
  }, []);
  const {
    data: localWalletProfile
  } = useBapProfile(hasLocalBackup ? localWalletBapId : null);
  const localWallet = describeLocalWallet(hasLocalBackup ? localWalletBapId : null, localWalletProfile);
  const {
    isSupported: prfSupported,
    hasSealedBackup,
    unlock: prfUnlock,
    isWorking: prfWorking,
    cancelUnlock: cancelPRFUnlock,
    clearError: clearPRFError,
    error: prfError
  } = usePRF();
  const [loading, setLoading] = useState(false);
  const [passkeySigningIn, setPasskeySigningIn] = useState(false);
  const [passkeySignedIn, setPasskeySignedIn] = useState(false);
  const [passkeySignInSupported, setPasskeySignInSupported] = useState(false);
  useEffect(() => {
    setPasskeySignInSupported(typeof PublicKeyCredential !== "undefined");
  }, []);
  const [error, setError] = useState("");
  const [showSetPasswordForm, setShowSetPasswordForm] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [importedUnencryptedBackup, setImportedUnencryptedBackup] = useState<IdentityBackup | null>(null);
  const [showSyncDialog, setShowSyncDialog] = useState(false);
  const [socialProviderLoading, setSocialProviderLoading] = useState<OAuthProvider | null>(null);
  const [isMac, setIsMac] = useState(false);
  const [preferPasswordUnlock, setPreferPasswordUnlock] = useState(false);
  const [showAllSignInOptions, setShowAllSignInOptions] = useState(false);
  // The imported key decrypted fine but no Sigma account exists for it.
  const [unregisteredKey, setUnregisteredKey] = useState(false);
  const [creatingAccount, setCreatingAccount] = useState(false);
  const [signerReturnComplete, setSignerReturnComplete] = useState(false);

  // Extract URL parameters FIRST (before using in hooks)
  const clientId = searchParams.get("client_id");
  const returnToParam = searchParams.get("return_to") ?? searchParams.get("redirect");
  const redirectUri = searchParams.get("redirect_uri");
  const state = searchParams.get("state");
  const codeChallenge = searchParams.get("code_challenge");
  const codeChallengeMethod = searchParams.get("code_challenge_method");
  const isSwitchAccountFlow = searchParams.get("switch_account") === "1";
  const signupHref = useMemo(() => {
    const query = searchParams.toString();
    return query ? `/signup?${query}` : "/signup";
  }, [searchParams]);

  // TanStack Query hooks for data fetching (after extracting clientId)
  // Only query OAuth client info if we have a client_id (OAuth flow)
  const oauthClientQuery = useOAuthClientInfo(clientId || null, serverData?.initialClient);
  const clientProfileQuery = useBapProfile(clientId ? oauthClientQuery.data?.ownerBapId || null : null);

  // Derived state from queries
  const oauthClient = oauthClientQuery.data || null;
  const clientProfile = clientProfileQuery.data || null;
  const loadingClientInfo = oauthClientQuery.isLoading || clientProfileQuery.isLoading;

  // White-label theme fetched from theme-token registry
  const {
    theme: clientTheme
  } = useClientTheme(oauthClient?.themeOrigin, serverData?.serverTheme);

  // Initialize sync status with predefined steps
  const initialSyncSteps: SyncStep[] = [{
    id: "decrypt",
    label: "Decrypting backup...",
    status: "pending"
  }, {
    id: "discover",
    label: "Verifying backup...",
    status: "pending"
  }, {
    id: "register",
    label: "Syncing identities...",
    status: "pending"
  }, {
    id: "complete",
    label: "Finalizing...",
    status: "pending"
  }, {
    id: "redirect",
    label: "Redirecting...",
    status: "pending"
  }];
  const {
    steps: syncSteps,
    updateStep
  } = useSyncStatus(initialSyncSteps);
  const restoreCallbackURL = useMemo(() => buildRestoreCallbackURL(typeof window === "undefined" ? "" : window.location.origin, searchParams, returnToParam), [searchParams, returnToParam]);
  const [recoveryPassword, setRecoveryPassword] = useState("");
  const [recoveryReady, setRecoveryReady] = useState(false);
  const [recoveryUnlocking, setRecoveryUnlocking] = useState(false);
  const recoveryAttempt = useRef<AbortController | null>(null);
  const recoveryBaseline = useRef<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    authClient.getSession().then(result => {
      if (!cancelled && result.data?.user) {
        setRecoveryReady(true);
      }
    }).catch(() => {
      /* The explicit restore action reports session errors. */
    });
    return () => {
      cancelled = true;
    };
  }, []);
  const cancelRecovery = useCallback(() => {
    recoveryAttempt.current?.abort();
    recoveryAttempt.current = null;
    setSocialProviderLoading(null);
    setRecoveryPassword("");
    setRecoveryReady(false);
    clearPendingRecoveryMethod();
  }, []);
  useEffect(() => () => recoveryAttempt.current?.abort(), []);
  const finishCloudRecovery = useCallback(async (unlock = true) => {
    const attempt = recoveryAttempt.current;
    if (!attempt) {
      throw new Error("Recovery cancelled.");
    }
    setRecoveryUnlocking(true);
    try {
      const fresh = await authClient.getSession({
        query: {
          disableCookieCache: true
        }
      });
      if (!fresh.data?.user) {
        throw new Error("Sign-in was not completed. Please try again.");
      }
      if (attempt.signal.aborted) {
        throw new Error("Recovery cancelled.");
      }
      const cloudBackup = await fetchCloudBackup();
      if (attempt.signal.aborted) {
        return;
      }
      setRecoveryReady(true);
      // Authenticate with the provider before asking for the backup password.
      // The password is used only by this browser's explicit unlock action.
      if (!(unlock && recoveryPassword)) {
        return;
      }
      const redirectTo = await unlockCloudBackup({
        cloudBackup,
        password: recoveryPassword,
        returnTo: new URL(restoreCallbackURL, window.location.origin).searchParams.get("return_to") || returnToParam,
        isCurrent: () => !attempt.signal.aborted && recoveryAttempt.current === attempt && recoveryBaseline.current === recoverySource()
      });
      setRecoveryPassword("");
      window.location.href = redirectTo || "/";
    } finally {
      setRecoveryUnlocking(false);
    }
  }, [recoveryPassword, returnToParam, restoreCallbackURL]);

  // Email OTP sign-in hook. Receives restoreCallbackURL so it can mirror the
  // social sign-in flow's callback routing for users with no local backup.
  const {
    showEmailSignIn,
    setShowEmailSignIn,
    emailForOtp,
    setEmailForOtp,
    otpCode,
    setOtpCode,
    otpSent,
    setOtpSent,
    emailAuthPending,
    handleSendEmailOtp,
    handleEmailOtpSignIn,
    resetEmailOtp
  } = useEmailOtpSignIn(hasLocalBackup && !isSwitchAccountFlow, setError, restoreCallbackURL, () => finishCloudRecovery(false), () => {
    recoveryAttempt.current?.abort();
    recoveryAttempt.current = new AbortController();
    recoveryBaseline.current = recoverySource();
    return recoveryAttempt.current.signal;
  });

  // When a backup lands via import, route the user to the password-unlock
  // form even if a passkey (PRF) credential exists on this device — an import
  // is an explicit request to sign in with the backup file, and the imported
  // backup can only be opened with its password.
  const markBackupImported = useCallback((v: boolean) => {
    setHasLocalBackup(v);
    setUnregisteredKey(false);
    if (v) {
      setPreferPasswordUnlock(true);
      setShowAllSignInOptions(false);
    }
  }, []);

  // Backup import hook
  const {
    importBackupText,
    importBackupFile,
    handleImportBackup,
    pendingEncryptedBackup,
    setPendingEncryptedBackup,
    isPendingCandidate
  } = useBackupImport(setError, markBackupImported, setImportedUnencryptedBackup, setShowSetPasswordForm);
  const handleSocialSignIn = useCallback(async (provider: OAuthProvider) => {
    setRecoveryPassword("");
    setRecoveryReady(false);
    recoveryAttempt.current?.abort();
    const attempt_0 = new AbortController();
    recoveryAttempt.current = attempt_0;
    recoveryBaseline.current = recoverySource();
    setSocialProviderLoading(provider);
    setError("");
    try {
      const errorCallback = new URL(window.location.href);
      errorCallback.searchParams.set("provider", provider);
      const providerURL = await requestRecoveryProviderURL(provider, restoreCallbackURL, errorCallback.toString());
      if (attempt_0.signal.aborted) {
        return;
      }
      stageRecoveryMethod(`provider:${provider}`);
      window.location.assign(providerURL);
    } catch (error_0) {
      if (!attempt_0.signal.aborted) {
        setError(recoveryErrorMessage(error_0));
      }
    } finally {
      if (recoveryAttempt.current === attempt_0) {
        setSocialProviderLoading(null);
      }
    }
  }, [restoreCallbackURL]);
  useEffect(() => {
    if (!window.location.search.includes("login_success=")) {
      clearPendingRecoveryMethod();
    }
  }, []);
  useEffect(() => {
    if (typeof navigator === "undefined") {
      return;
    }
    setIsMac(MAC_PLATFORM_REGEX.test(navigator.platform));
  }, []);
  useEffect(() => {
    hydratePRFCredentialFromServer().catch(() => {
      logger.warn({
        operation: "prf_credential_hydration"
      }, "PRF credential hydration failed on login");
    });
  }, []);
  useEffect(() => {
    const errorMessage = getOAuthCallbackErrorMessage({
      error: searchParams.get("error"),
      errorDescription: searchParams.get("error_description"),
      provider: searchParams.get("provider")
    });
    if (errorMessage) {
      setError(errorMessage);
      setSocialProviderLoading(null);
    }
  }, [searchParams]);
  useImportShortcut(isMac, loading);

  // Validate OAuth client exists if OAuth params are present
  useEffect(() => {
    if (!clientId || oauthClientQuery.isLoading || oauthClient || oauthClientQuery.error) {
      return;
    }
    const errorMessage_0 = getOAuthClientErrorMessage(redirectUri);
    logger.error({
      operation: "oauth_client_lookup"
    }, "OAuth client not found in database");
    setError(errorMessage_0);
  }, [clientId, redirectUri, oauthClient, oauthClientQuery.isLoading, oauthClientQuery.error]);
  const importedCandidateRef = useRef(importedUnencryptedBackup);
  importedCandidateRef.current = importedUnencryptedBackup;
  const handleSaveImportedWallet = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!importedUnencryptedBackup) {
      return;
    }
    await encryptAndSaveBackup(importedUnencryptedBackup, newPassword, confirmPassword, {
      setLoading,
      setError,
      setImportedUnencryptedBackup,
      setShowSetPasswordForm,
      setNewPassword,
      setConfirmPassword,
      // Must route to the password form: the post-save auto-submit targets
      // the form rendered by UnlockContent, which the passkey view hides.
      setHasLocalBackup: markBackupImported,
      setPassword,
      setPendingEncryptedBackup,
      isCandidateCurrent: () => importedCandidateRef.current === importedUnencryptedBackup
    });
  };
  const oauthParams = useMemo(() => ({
    clientId,
    redirectUri,
    state,
    codeChallenge,
    codeChallengeMethod,
    scope: searchParams.get("scope"),
    responseType: searchParams.get("response_type")
  }), [clientId, redirectUri, state, codeChallenge, codeChallengeMethod, searchParams]);
  const handleSubmit = useCallback(async (e_0: React.FormEvent) => {
    e_0.preventDefault();
    if (clientId && !oauthClient) {
      setError("OAuth client not found. Cannot proceed with authentication.");
      return;
    }
    setLoading(true);
    setError("");
    setUnregisteredKey(false);
    setShowSyncDialog(true);
    try {
      const result_0 = await executePasswordLogin(password, updateStep, oauthParams, returnToParam, pendingEncryptedBackup ?? undefined, () => !!pendingEncryptedBackup && isPendingCandidate(pendingEncryptedBackup));
      if (result_0 === "signer_return") {
        setSignerReturnComplete(true);
        setShowSyncDialog(false);
      }
    } catch (loginError) {
      if (loginError instanceof LoginFlowError && loginError.code === UNREGISTERED_IDENTITY_CODE && loginError.canEnroll) {
        // The key is real and the password was right; there is just no
        // account for it yet. Offer to create one instead of a dead end.
        setUnregisteredKey(true);
        setError("");
      } else {
        setError(reportPasswordLoginFailure(loginError));
      }
      setShowSyncDialog(false);
    } finally {
      setLoading(false);
    }
  }, [clientId, oauthClient, password, updateStep, oauthParams, returnToParam, pendingEncryptedBackup, isPendingCandidate]);
  const handlePhraseRestored = useCallback((result_1: {
    bapId: string;
    redirectURI?: string;
  }) => {
    rememberSuccessfulSignIn("phrase");
    if (result_1.redirectURI) {
      window.location.href = result_1.redirectURI;
      return;
    }
    const outcome = handlePostSignInRedirect({
      id: result_1.bapId
    }, oauthParams, returnToParam, updateStep);
    if (outcome === "signer_return") {
      setSignerReturnComplete(true);
    }
  }, [oauthParams, returnToParam, updateStep]);

  /** Enroll the unregistered key: the same sign-up /signup performs. */
  const handleCreateAccountWithKey = useCallback(async () => {
    setCreatingAccount(true);
    setError("");
    setShowSyncDialog(true);
    try {
      const result_2 = await executePasswordLogin(password, updateStep, oauthParams, returnToParam, pendingEncryptedBackup ?? undefined, () => pendingEncryptedBackup ? isPendingCandidate(pendingEncryptedBackup) : true, {
        enrollNewProfile: true
      });
      if (result_2 === "signer_return") {
        setSignerReturnComplete(true);
        setShowSyncDialog(false);
      }
    } catch (loginError_0) {
      clearPendingRecoveryMethod();
      setError(reportPasswordLoginFailure(loginError_0));
      setShowSyncDialog(false);
    } finally {
      setCreatingAccount(false);
    }
  }, [password, updateStep, oauthParams, returnToParam, pendingEncryptedBackup, isPendingCandidate]);
  const handleWalletSignIn = useCallback(async (bapId: string) => {
    if (clientId && !oauthClient) {
      throw new Error("OAuth client is unavailable");
    }
    let setActive = await authClient.organization.setActive({
      organizationId: bapId
    });
    if (setActive.error) {
      setActive = await authClient.organization.setActive({
        organizationSlug: bapId
      });
    }
    if (setActive.error) {
      throw new Error("The linked Sigma identity could not be selected");
    }
    rememberSuccessfulSignIn("wallet");
    const result_3 = handlePostSignInRedirect({
      id: bapId
    }, oauthParams, returnToParam, updateStep);
    if (result_3 === "signer_return") {
      setSignerReturnComplete(true);
    }
  }, [clientId, oauthClient, oauthParams, returnToParam, updateStep]);
  const {
    isFileDragActive
  } = useFileDrop({
    enabled: true,
    onFile: importBackupFile
  });
  useEffect(() => {
    const pending = sessionStorage.getItem(STORAGE_KEYS.PENDING_IMPORT_BACKUP);
    if (!pending) {
      return;
    }
    sessionStorage.removeItem(STORAGE_KEYS.PENDING_IMPORT_BACKUP);
    importBackupText(pending).catch(() => {
      // Import errors are shown by importBackupText itself
    });
  }, [importBackupText]);
  const handlePasskeySignIn = useCallback(async () => {
    setPasskeySigningIn(true);
    setError("");
    clearPRFError();
    try {
      const result_4 = await authClient.signIn.passkey();
      if (result_4.error) {
        if ("code" in result_4.error && (result_4.error.code === "AUTH_CANCELLED" || result_4.error.code === "ERROR_CEREMONY_ABORTED")) {
          return;
        }
        setError("Passkey sign-in wasn't completed. Try again or choose another method.");
        return;
      }
      if (!result_4.data?.user) {
        throw new Error("Missing authenticated session");
      }
      setPasskeySignedIn(true);
      setRecoveryReady(true);
      recoveryAttempt.current?.abort();
      recoveryAttempt.current = new AbortController();
      recoveryBaseline.current = recoverySource();
      setShowAllSignInOptions(false);
      setPreferPasswordUnlock(false);
      await hydratePRFCredentialFromServer();
    } catch {
      setError("Couldn't finish passkey sign-in. Try again or use another recovery method.");
    } finally {
      setPasskeySigningIn(false);
    }
  }, [clearPRFError]);
  const showPasskeyPasswordRecovery = useCallback(() => {
    cancelPRFUnlock();
    clearPRFError();
    setLoading(false);
    recoveryAttempt.current?.abort();
    recoveryAttempt.current = new AbortController();
    recoveryBaseline.current = recoverySource();
    setShowAllSignInOptions(true);
  }, [cancelPRFUnlock, clearPRFError]);
  const handlePRFUnlock = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const decryptedStr = await prfUnlock();
      if (!decryptedStr) {
        setLoading(false);
        return;
      }
      validateIdentityBackup(JSON.parse(decryptedStr));
      sessionStorage.setItem(STORAGE_KEYS.DECRYPTED_BACKUP, decryptedStr);
      setShowSyncDialog(true);
      const decryptedData = validateIdentityBackup(JSON.parse(decryptedStr));
      const identity = extractIdentityFromBackup(decryptedData);
      logger.info({
        operation: "passkey_unlock_sign_in"
      }, "Signing in via passkey unlock");
      const signInResult = await performSignIn(decryptedData, updateStep);
      const finalIdentity = await postSignInIdentitySync(identity, "", signInResult);
      completeRecoveryMethod();
      const redirectResult = handlePostSignInRedirect(finalIdentity, oauthParams, returnToParam, updateStep);
      if (redirectResult === "signer_return") {
        setSignerReturnComplete(true);
        setShowSyncDialog(false);
      }
    } catch {
      setError("Passkey sign-in failed. Please try again.");
      setShowSyncDialog(false);
    } finally {
      setLoading(false);
    }
  }, [prfUnlock, updateStep, oauthParams, returnToParam]);
  const handleClearBackup = useCallback(() => {
    if (pendingEncryptedBackup) {
      setPendingEncryptedBackup(null);
      setPassword("");
      setError("");
      setHasLocalBackup(!!localStorage.getItem(STORAGE_KEYS.ENCRYPTED_BACKUP));
      return;
    }
    localStorage.removeItem(STORAGE_KEYS.ENCRYPTED_BACKUP);
    localStorage.removeItem(STORAGE_KEYS.CURRENT_IDENTITY);
    sessionStorage.removeItem(STORAGE_KEYS.DECRYPTED_BACKUP);
    sessionStorage.removeItem(STORAGE_KEYS.TEMP_PASSWORD);
    setHasLocalBackup(false);
    setLocalWalletBapId(null);
    setPreferPasswordUnlock(false);
    setShowAllSignInOptions(false);
    setPassword("");
    setError("");
  }, [pendingEncryptedBackup, setPendingEncryptedBackup]);
  const handleCancelSetPassword = useCallback(() => {
    importedCandidateRef.current = null;
    setShowSetPasswordForm(false);
    setImportedUnencryptedBackup(null);
    setHasLocalBackup(!!localStorage.getItem(STORAGE_KEYS.ENCRYPTED_BACKUP));
    setNewPassword("");
    setConfirmPassword("");
    setError("");
  }, []);
  const hasPRFBackup = recoveryReady && (!isSwitchAccountFlow || passkeySignedIn) && prfSupported && hasSealedBackup;
  const shouldOfferLocalBackup = hasLocalBackup && !isSwitchAccountFlow && !passkeySignedIn;
  return {
    clientProfile,
    clientProfileError: clientProfileQuery.error,
    clientTheme,
    confirmPassword,
    emailAuthPending,
    emailForOtp,
    error,
    handleCancelSetPassword,
    handleClearBackup,
    handleEmailOtpSignIn,
    handleImportBackup,
    handlePRFUnlock,
    handlePasskeySignIn,
    showPasskeyPasswordRecovery,
    passkeySigningIn,
    passkeySignedIn,
    passkeySignInSupported,
    handleSaveImportedWallet,
    handleSendEmailOtp,
    handleSocialSignIn,
    handleCreateAccountWithKey,
    handleSubmit,
    handleWalletSignIn,
    handlePhraseRestored,
    hasPRFBackup,
    creatingAccount,
    unregisteredKey,
    isFileDragActive,
    isMac,
    loading,
    loadingClientInfo,
    localWallet,
    newPassword,
    oauthClient,
    oauthClientError: oauthClientQuery.error,
    otpCode,
    otpSent,
    password,
    preferPasswordUnlock,
    prfError,
    prfWorking,
    resetEmailOtp,
    setConfirmPassword,
    setEmailForOtp,
    setNewPassword,
    setOtpCode,
    setOtpSent,
    setPassword,
    setPreferPasswordUnlock,
    setShowAllSignInOptions,
    setShowEmailSignIn,
    shouldOfferLocalBackup,
    showAllSignInOptions,
    showEmailSignIn,
    showSetPasswordForm,
    showSyncDialog,
    signerReturnComplete,
    signupHref,
    socialProviderLoading,
    socialSignInIsPending: Boolean(socialProviderLoading) || recoveryUnlocking,
    storageChecked,
    syncSteps,
    recoveryReady,
    handleRecoveryRetry: () => {
      recoveryAttempt.current?.abort();
      recoveryAttempt.current = new AbortController();
      recoveryBaseline.current = recoverySource();
      return finishCloudRecovery().catch((error_1: unknown) => setError(recoveryErrorMessage(error_1)));
    },
    recoveryPassword,
    setRecoveryPassword,
    cancelRecovery,
    walletSignInDisabled: Boolean(clientId && !oauthClient)
  };
}